import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import moment from "moment";
import {
  Datagrid,
  DateInput,
  FunctionField,
  NumberField,
  TextField,
  useListContext,
} from 'react-admin';
import { Permission, ReportList } from "../../components";
import { PERMISSIONS } from "../../constants";

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold',
};

const formatNumber = (v: any, digits = 2) =>
  typeof v === 'number' ? v.toLocaleString('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : '--';

const FillinInfoByError = ({ descricao, fillin }: { descricao: string; fillin: any }) => {
  if (!fillin) return <span style={{ color: '#999' }}>--</span>;

  if (descricao?.includes('bico')) {
    return <span><strong>Bico:</strong> {fillin.hoseNumber ?? '--'}</span>;
  }
  if (descricao?.includes('Encerrante')) {
    return <span><strong>Encerrante:</strong> {formatNumber(fillin.encerrante, 2)}</span>;
  }
  if (descricao?.includes('Quantidade') || descricao?.includes('valor')) {
    return (
      <span>
        <strong>Qtd:</strong> {formatNumber(fillin.amount, 3)} L |{' '}
        <strong>Valor:</strong> R$ {formatNumber(fillin.value, 2)}
      </span>
    );
  }
  return <span style={{ color: '#999' }}>--</span>;
};

const FillinLink = ({ id }: { id: string | null }) => {
  if (!id) return <span>-</span>;
  return (
    <a
      href={`/#/fillins/${id}/show`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#1976d2', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      {id.substring(0, 8)}...
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </a>
  );
};

const ListRdpContingencies = () => {
  const { data } = useListContext();

  let errosReconciliacao: any[] = [];
  let diferencas: any[] = [];

  if (data && data[0]) {
    errosReconciliacao = (data[0].errosReconciliacao || []).map((e, i) => ({ ...e, id: i }));
    diferencas = data[0].diferencas || [];
  }

  return (
    <>
      <Typography sx={titleSx} variant="h6">
        Erros de Conciliação ({errosReconciliacao.length})
      </Typography>
      <Datagrid
        bulkActionButtons={false}
        data={errosReconciliacao}
        total={errosReconciliacao.length}
        style={{ marginBottom: 40 }}
      >
        <FunctionField
          label="Dados do Abastecimento"
          sortable={false}
          render={(record: any) => (
            <FillinInfoByError descricao={record?.descricao} fillin={record?.fillin} />
          )}
        />
        <FunctionField
          source="idPontua"
          label="Abastecimento"
          render={(record: any) => <FillinLink id={record?.idPontua} />}
        />
        <TextField source="descricao" label="Descrição" sortable={false} />
        <FunctionField
          label="Unificação"
          sortable={false}
          render={(record: any) => {
            const u = record?.unification;
            if (!u) return <span style={{ color: '#999' }}>--</span>;
            if (u.mergedIntoFillinId) {
              return (
                <span style={{ color: '#2e7d32' }}>
                  Unificado em <FillinLink id={u.mergedIntoFillinId} />
                </span>
              );
            }
            return (
              <Chip
                icon={<CheckCircleIcon />}
                color="success"
                size="small"
                label={`Unificado (${u.unifiedFillinCount})`}
              />
            );
          }}
        />
      </Datagrid>

      <Typography sx={titleSx} variant="h6">
        Diferenças por Posto ({diferencas.length})
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 1 }}>
        <Chip icon={<CheckCircleIcon />} label="No PontuaX" color="success" size="small" />
        <Chip icon={<CancelIcon />} label="Fora do PontuaX" color="error" size="small" />
        <Chip icon={<CancelIcon />} label="Não encontrado" size="small" />
      </Box>
      {diferencas.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', my: 2 }}>
          Nenhuma diferença encontrada.
        </Typography>
      )}
      {diferencas.map((dif, i) => {
        const itens = (dif.itens || []).map((item, j) => ({ ...item, id: j }));
        return (
          <Card key={i} variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ pb: '8px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {dif.empresa.nome}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CNPJ: {dif.empresa.cnpj}
                    {dif.placeName && ` | PontuaX: ${dif.placeName}`}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2">
                    Desconto: R$ {dif.totalDesconto} | Real: R$ {dif.descontoReal}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 'bold',
                      color: parseFloat(dif.totalDiferenca) < 0 ? 'error.main'
                        : parseFloat(dif.totalDiferenca) > 0 ? 'success.main' : undefined
                    }}
                  >
                    Diferença: R$ {dif.totalDiferenca}
                  </Typography>
                </Box>
              </Box>
              {itens.length > 0 && (
                <Datagrid
                  bulkActionButtons={false}
                  data={itens}
                  total={itens.length}
                >
                  <FunctionField
                    label="Tipo"
                    render={(record: any) => (
                      <Chip
                        label={record?.tipo === 'com_diferenca' ? 'Diferença' : 'Não encontrado'}
                        color={record?.tipo === 'com_diferenca' ? 'warning' : 'error'}
                        size="small"
                      />
                    )}
                  />
                  <TextField source="produto" label="Produto" sortable={false} />
                  <TextField source="bico" label="Bico" sortable={false} />
                  <NumberField
                    source="quantidade"
                    label="Qtd (L)"
                    sortable={false}
                    options={{ maximumFractionDigits: 3 }}
                  />
                  <NumberField
                    source="valor"
                    label="Valor"
                    sortable={false}
                    options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                  />
                  <NumberField
                    source="encerrante"
                    label="Encerrante"
                    sortable={false}
                    options={{ maximumFractionDigits: 2 }}
                  />
                  <NumberField
                    source="desconto"
                    label="Desconto"
                    sortable={false}
                    options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }}
                  />
                  <FunctionField
                    source="diferenca"
                    label="Diferença"
                    render={(record: any) => {
                      const val = record?.diferenca;
                      const color = val < 0 ? '#d32f2f' : val > 0 ? '#2e7d32' : undefined;
                      return <span style={{ color, fontWeight: 'bold' }}>R$ {val?.toFixed(2)}</span>;
                    }}
                  />
                  <FunctionField
                    label="Origem"
                    render={(record: any) => {
                      const match = record?.redshiftMatch;
                      if (!match?.found) return <Chip icon={<CancelIcon />} label="Não encontrado" size="small" />;
                      return (
                        <Chip
                          icon={match.reserveId ? <CheckCircleIcon /> : <CancelIcon />}
                          label={match.reserveId ? 'No PontuaX' : 'Fora do PontuaX'}
                          color={match.reserveId ? 'success' : 'error'}
                          size="small"
                        />
                      );
                    }}
                  />
                  <FunctionField
                    label="Fillin"
                    render={(record: any) => <FillinLink id={record?.redshiftMatch?.fillinId} />}
                  />
                </Datagrid>
              )}
            </CardContent>
          </Card>
        );
      })}
    </>
  );
};

const RdpContingencies = (props: any) => {
  const listFilters = [
    <DateInput
      key="date"
      source="date"
      label="Data da Venda"
      alwaysOn
    />,
  ];

  return (
    <Permission permission={PERMISSIONS.ADMIN}>
      <ReportList
        {...props}
        basePath="rdp-contingencies"
        resource="rdp-contingencies"
        title="Contingências RDP"
        filters={listFilters}
        perPage={1}
        disablePrint
        showPagination={false}
        filterDefaultValues={{
          date: moment().subtract(1, "day").format("YYYY-MM-DD"),
        }}
      >
        <ListRdpContingencies />
      </ReportList>
    </Permission>
  );
};

export default RdpContingencies;
